import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBillingStore } from '@/stores/billingStore';
import Icon from '../components/Icon';

export const BillingPage = () => {
  const {
    activeSubscription,
    creditsBalance,
    creditsUsedThisMonth,
    payments,
    isLoading,
    error,
    fetchBillingStatus,
    fetchPayments,
  } = useBillingStore();

  useEffect(() => {
    fetchBillingStatus();
    fetchPayments();
  }, [fetchBillingStatus, fetchPayments]);

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-6">Billing</h1>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Subscription Card */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="card_membership" size={26} className="text-primary" />
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">Subscription</h2>
          </div>
          {activeSubscription ? (
            <div>
              <p className="text-2xl font-bold text-slate-800 dark:text-slate-200">{activeSubscription.planName}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {activeSubscription.pricePerMonth.toFixed(2)}/month
              </p>
              <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                Active
              </div>
            </div>
          ) : (
            <div>
              <p className="text-slate-500 dark:text-slate-400">No active subscription</p>
              <Link
                to="/billing/plans"
                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80"
              >
                View Plans
                <Icon name="arrow_forward" size={16} />
              </Link>
            </div>
          )}
        </div>

        {/* Credits Card */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="toll" size={26} className="text-amber-500" />
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">Credits</h2>
          </div>
          <p className="text-3xl font-bold text-slate-800 dark:text-slate-200">{creditsBalance}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {creditsUsedThisMonth} used this month
          </p>
          <Link
            to="/billing/credits"
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80"
          >
            Buy Credits
            <Icon name="arrow_forward" size={16} />
          </Link>
        </div>

        {/* Quick Actions Card */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="bolt" size={26} className="text-slate-500" />
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">Quick Actions</h2>
          </div>
          <div className="space-y-2">
            <Link
              to="/billing/plans"
              className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-primary transition-colors"
            >
              <Icon name="upgrade" size={16} />
              Upgrade Plan
            </Link>
            <Link
              to="/billing/credits"
              className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-primary transition-colors"
            >
              <Icon name="add_circle" size={16} />
              Buy Credits
            </Link>
          </div>
        </div>
      </div>

      {/* Recent Payments */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">Recent Payments</h2>
        </div>
        {payments.length > 0 ? (
          <div className="divide-y divide-slate-200 dark:divide-slate-700">
            {payments.slice(0, 5).map((payment) => (
              <div key={payment.id} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{payment.paymentType}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {new Date(payment.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    {payment.total.toFixed(2)} {payment.currency || 'EUR'}
                  </p>
                  {payment.paidAt ? (
                    <span className="text-xs text-green-600 dark:text-green-400">Paid</span>
                  ) : payment.status && (
                    <span className={`text-xs ${payment.status === 'Pending'
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-red-600 dark:text-red-400'}`}>{payment.status}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center text-slate-500 dark:text-slate-400">
            No payments yet
          </div>
        )}
      </div>
    </div>
  );
};
